
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AccountSelect from "./AccountSelect";
import Custom from "../../../Components/Buttons/Custom";
import SVG from "../../../Components/SVG";
import CalculatorModal from "../../Calculator/CalculatorModal";
import TradingView from "./TradingView";

import SetOrder from "./SetOrder";
import InfoCard from "./InfoCard";
import SymbolSelect from "./SymbolSelect";
import Tableau from "./Tableau";
import OrderList from "./OrderList";

import { API_BASE_URL } from "../../../config/api";
import {
  formatTradeSymbol,
  getTradeItemSymbol,
  getTradeSymbolIcons,
  isBlockedTradeSymbol,
  normalizeSymbolCode,
} from "../tradeUtils";

export default function ContainerAll({
  accountData,
  marketData,
  symbol,
  setSymbol,
  calculatorTradeContext = null,
  fetchWithAuthRefresh = null,
  marketSocketSession = 0,
  onCalculatorInsert = null,
  registerMarketSocketHandler = null,
  sendMarketSocketMessage = null,
  calculatorInsertRequest,
  symbolCatalog,
  selectedSide,
  onSelectedSideChange,
  onDrawdownDataChange,
  chartElement = null,
  onTradeNotification = null,
}) {
  const navigate = useNavigate();
  const { accountId: accountCode } = useParams();

  const [accountOptions, setAccountOptions] = useState(null);
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);

  const fetchAccountData = async () => {
    try {
      let response = await fetch(
        `${API_BASE_URL}challenges/trader/accounts`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      if (response.status === 401) {
        try {
          const refreshResponse = await fetch(
            `${API_BASE_URL}auth/trader/jwt/refresh/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refresh: localStorage.getItem("refresh"),
              }),
            },
          );

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            navigate("/signin");
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(
            `${API_BASE_URL}challenges/trader/accounts`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
            },
          );
        } catch (err) {
          console.error("Error refreshing token:", err);
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
      setAccountOptions(data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  const fetchUserData = async () => {
    try {
      let response = await fetch(
        `${API_BASE_URL}auth/trader/info/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );

      if (response.status === 401) {
        try {
          const refreshResponse = await fetch(
            `${API_BASE_URL}auth/trader/jwt/refresh/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refresh: localStorage.getItem("refresh"),
              }),
            },
          );

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            navigate("/signin");
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(
            `${API_BASE_URL}auth/trader/info/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
            },
          );
        } catch (err) {
          console.error("Error refreshing token:", err);
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("User Data:", data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchAccountData();
    fetchUserData();
  }, []);

  const tradingViewMemo = useMemo(() => {
    return <TradingView symbol={symbol} />;
  }, [symbol]);

  const symbolOptions = useMemo(() => {
    const marketSymbols =
      Array.isArray(symbolCatalog) && symbolCatalog.length
        ? symbolCatalog
        : Array.isArray(marketData?.symbols)
          ? marketData.symbols
          : [];

    return marketSymbols
      .map((item) => {
        const itemSymbol = getTradeItemSymbol(item);
        if (isBlockedTradeSymbol(itemSymbol)) return null;

        const normalized = normalizeSymbolCode(itemSymbol);
        if (!normalized) return null;

        return {
          value: normalized,
          label: formatTradeSymbol(itemSymbol),
          fullname: String(
            item?.fullname ?? item?.full_name ?? item?.fullName ?? "",
          ),
          svg: item?.svg ?? "",
          icons: getTradeSymbolIcons(itemSymbol),
        };
      })
      .filter(Boolean);
  }, [marketData, symbolCatalog]);

  return (
    <div className="w-full h-full grid grid-cols-10 lg:grid-cols-10 gap-2.5">
      {/* Set Order And CalculatorButton */}
      <section className="col-start-1 col-span-6 lg:col-span-2 row-start-1">
        <div className="h-full flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setIsCalculatorModalOpen(true)}
            className="h-fit inline-flex items-center justify-center gap-3 rounded-lg bg-transparent px-3 py-2.5 text-nowrap text-xs sm:text-sm text-zi-text-light dark:text-zi-text-dark ring-1 ring-inset ring-zi-border-dark dark:ring-zi-border-light/50 hocus:ring-2"
          >
            <SVG
              name="calculator"
              classname="size-5 stroke-zi-text-light dark:stroke-zi-text-dark"
            />
            <span>ماشین حساب هوشمند</span>
          </button>
          <div className="w-full h-full rounded-2xl p-2.5 flex flex-col gap-2.5 bg-zi-bg2-light dark:bg-zi-bg2-dark">
            <SymbolSelect
              options={symbolOptions}
              value={symbol}
              onChange={setSymbol}
              name="symbol"
            />
            <SetOrder
              accountData={accountData}
              calculatorInsertRequest={calculatorInsertRequest}
              marketData={marketData}
              symbol={symbol}
              selectedSide={selectedSide}
              onSelectedSideChange={onSelectedSideChange}
              onTradeNotification={onTradeNotification}
            />
          </div>
        </div>
      </section>

      {/* Tableau And AccountButton */}
      <section className="col-start-7 lg:col-start-9 col-span-4 lg:col-span-2 row-start-1">
        <div className="h-full flex flex-col gap-2.5">
          <AccountSelect
            options={accountOptions ? accountOptions.live : []}
            defaultValue={accountCode}
            symbol={symbol}
          />
          <Tableau marketData={marketData} symbol={symbol} />
        </div>
      </section>

      {/* Order List */}
      <section className="col-start-1 lg:col-start-3 col-span-full lg:col-span-8 row-start-2">
        <OrderList
          accountData={accountData}
          marketData={marketData}
          symbol={symbol}
          className="max-h-[60dvh] mb:max-h-none mb:h-[346px] lg:h-full"
        />
      </section>

      {/* Info Card And DetailButton */}
      <section className="col-start-1 col-span-full lg:col-span-2 row-start-3 lg:row-start-2">
        <div
          className={`w-full rounded-2xl p-2.5 flex flex-col items-center gap-5 bg-zi-bg2-light dark:bg-zi-bg2-dark`}
        >
          <InfoCard
            accountData={accountData}
            onDrawdownDataChange={onDrawdownDataChange}
          />
          <Custom
            text="مشاهده جزئیات حساب"
            href={`/challenge/account/${accountCode}/overview`}
            className="!rounded-full !px-5 !py-2.5 w-full max-w-64 text-nowrap text-zi-text-dark bg-zi-buttonbg-primary hocus:opacity-85"
            textShadow={true}
          />
        </div>
      </section>

      {/* Trading View */}
      <section className="col-start-1 lg:col-start-3 col-span-full lg:col-span-6 row-start-4 lg:row-start-1">
        <div className="h-[70dvh] lg:h-full">
          {/* {chartElement || tradingViewMemo} */}
          {tradingViewMemo}
        </div>
      </section>

      {/* Calculator Modal */}
      {isCalculatorModalOpen && (
        <CalculatorModal
          onClose={() => setIsCalculatorModalOpen(false)}
          onInsertValue={onCalculatorInsert}
          tradeContext={calculatorTradeContext}
        />
      )}
    </div>
  );
}
